import { useCallback, useEffect, useRef, useState } from "react"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import { shouldKeepPollingAmd } from "./amd"

const POLL_INTERVAL_MS = 1500

/**
 * Polls answering-machine detection state for a live Power Dialer call and
 * exposes the agent overrides ("it's a person", text, leave voicemail, hang up).
 *
 * `callSid` is the agent (browser) leg SID; the backend keys AMD state on it.
 */
export function useAmdCallStatus({ callSid, enabled }) {
  const axios = useAuthorizedAxiosClient()
  const [status, setStatus] = useState(null)
  const [isOverriding, setIsOverriding] = useState(false)
  const [overrideError, setOverrideError] = useState("")
  const statusRef = useRef(null)
  statusRef.current = status

  useEffect(() => {
    if (!enabled || !callSid) {
      setStatus(null)
      setOverrideError("")
      return undefined
    }

    let cancelled = false
    let timer = null

    async function poll() {
      try {
        const { data } = await axios.get(
          `/crm/api/calls/${encodeURIComponent(callSid)}/amd/`
        )
        if (cancelled) return
        setStatus(data)
        if (!shouldKeepPollingAmd(data)) return
      } catch (_) {
        // The Call row may not exist yet while the voice webhook catches up.
        if (cancelled) return
      }
      timer = window.setTimeout(poll, POLL_INTERVAL_MS)
    }

    poll()

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [axios, callSid, enabled])

  const override = useCallback(
    (action) => {
      if (!callSid) return Promise.resolve(null)
      setIsOverriding(true)
      setOverrideError("")
      return axios
        .post(`/crm/api/calls/${encodeURIComponent(callSid)}/amd/override/`, {
          action,
        })
        .then(({ data }) => {
          setStatus(data)
          return data
        })
        .catch((error) => {
          setOverrideError(
            parseApiErrorMessage(
              error?.response?.data,
              "Could not update this call."
            )
          )
          return null
        })
        .finally(() => setIsOverriding(false))
    },
    [axios, callSid]
  )

  return { status, statusRef, override, isOverriding, overrideError }
}

export default useAmdCallStatus
